import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';

export interface ITab {
  id: string;
  label: string;
  icon?: string;
  count?: number;
}

/**
 * Control segmentado que navega entre las pestañas internas de una sección.
 *
 * La piel es `.z-tabs` (styles.scss). Lo que aporta el componente es el patrón
 * ARIA de tablist: `aria-selected`, tabulador único (roving tabindex) y
 * navegación con flechas + Home/End.
 */
@Component({
  selector: 'app-section-tabs',
  standalone: false,
  templateUrl: './section-tabs.component.html',
  styleUrl: './section-tabs.component.scss',
})
export class SectionTabsComponent implements AfterViewInit, OnChanges {

  @Input() public tabs: Array<ITab> = [];
  @Input() public active = '';
  @Output() public activeChange = new EventEmitter<string>();

  @ViewChildren('tabButton') private buttons?: QueryList<ElementRef<HTMLButtonElement>>;

  /* -------------------------------------------------------------------------- */
  /*                            ANCHOR CICLO DE VIDA                            */
  /* -------------------------------------------------------------------------- */

  public ngAfterViewInit(): void {
    this.revealActive();
  }

  /** El padre puede cambiar la pestaña activa (o la lista) desde fuera. */
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes['active'] || changes['tabs']) {
      this.revealActive();
    }
  }

  /* -------------------------------------------------------------------------- */
  /*                              ANCHOR SELECCION                              */
  /* -------------------------------------------------------------------------- */

  public isActive(tab: ITab): boolean {
    return tab.id === this.active;
  }

  public select(tab: ITab): void {
    if (tab.id === this.active) {
      return;
    }

    this.active = tab.id;
    this.activeChange.emit(tab.id);
    this.revealActive();
  }

  /**
   * Un solo botón entra en el orden de tabulación; dentro se navega con flechas.
   * Si `active` aún no casa con ninguna pestaña, el tabulador cae en la primera
   * para que el control nunca quede inalcanzable con teclado.
   */
  public tabIndexFor(tab: ITab, index: number): number {
    const hasActive = this.tabs.some((item) => item.id === this.active);

    if (hasActive) {
      return this.isActive(tab) ? 0 : -1;
    }

    return index === 0 ? 0 : -1;
  }

  /* -------------------------------------------------------------------------- */
  /*                               ANCHOR TECLADO                               */
  /* -------------------------------------------------------------------------- */

  public onKeydown(event: KeyboardEvent, index: number): void {
    const last = this.tabs.length - 1;
    let target = -1;

    switch (event.key) {
      case 'ArrowRight': target = index === last ? 0 : index + 1; break;
      case 'ArrowLeft':  target = index === 0 ? last : index - 1; break;
      case 'Home':       target = 0; break;
      case 'End':        target = last; break;
      default: return;
    }

    const tab = this.tabs[target];

    if (!tab) {
      return;
    }

    event.preventDefault();
    this.select(tab);
    this.buttons?.get(target)?.nativeElement.focus();
  }

  /* -------------------------------------------------------------------------- */
  /*                             ANCHOR DESPLAZAMIENTO                          */
  /* -------------------------------------------------------------------------- */

  /**
   * Trae la pestaña activa a la vista dentro del carril horizontal.
   *
   * En un teléfono las cuatro pestañas no caben y `.z-tabs` se desplaza: al
   * cargar con la tercera activa, o al pasar a ella con las flechas, la pastilla
   * encendida quedaba fuera de cuadro y el control parecía no haber respondido.
   *
   * `nearest` en los dos ejes y nunca `center`: centrar mueve el carril aunque
   * la pestaña ya se vea y arrastra además el desplazamiento vertical de la
   * página, que es el salto brusco que se siente como un fallo. `nearest`
   * desplaza lo mínimo y no hace nada si ya está a la vista.
   */
  private revealActive(): void {
    const index: number = this.tabs.findIndex((tab: ITab) => tab.id === this.active);

    if (index < 0) {
      return;
    }

    // Un cambio de `active` se refleja en el DOM al terminar el ciclo: medir
    // antes daría la posición de la pestaña anterior.
    setTimeout(() => this.buttons?.get(index)?.nativeElement
      .scrollIntoView({ block: 'nearest', inline: 'nearest' }));
  }
}
